import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ShopLocation, ImageMetadata, RateLimitError } from '../types';
import { shopLocationService } from '../services/shopLocationService';
import { slideApiService } from '../services/slideApiService';
import LocationImageManager from './LocationImageManager';
import ImageCapture, { ImageCaptureHandle } from './ImageCapture';
import RateLimitModal from './RateLimitModal';

interface LocationEditModalProps {
  location: ShopLocation;
  shopId: string;
  onClose: () => void;
  onLocationUpdated: (updatedLocation: ShopLocation) => void;
}

const LocationEditModal: React.FC<LocationEditModalProps> = ({
  location,
  shopId,
  onClose,
  onLocationUpdated
}) => {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rotatingImages, setRotatingImages] = useState(false);
  const [croppingImages, setCroppingImages] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [slideCount, setSlideCount] = useState<number | undefined>(undefined);

  // Determine if this is create or edit mode
  const isCreateMode = !location.id;

  // Form data state
  const [name, setName] = useState(location.name || '');
  const [description, setDescription] = useState(location.description || '');
  const [images, setImages] = useState<ImageMetadata[]>(location.images || []);
  const [imageRotations, setImageRotations] = useState<{[imageId: string]: number}>({});
  const [imageCrops, setImageCrops] = useState<{[imageId: string]: { x: number; y: number; width: number; height: number }}>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isMobileCameraActive, setIsMobileCameraActive] = useState(false);
  const imageCaptureRef = useRef<ImageCaptureHandle>(null);

  const MAX_IMAGES = 5;

  const handleCameraStateChange = useCallback((isActive: boolean, isMobile: boolean) => {
    setIsMobileCameraActive(isActive && isMobile);
  }, []);

  // Fetch slide count for this location (only in edit mode)
  useEffect(() => {
    const fetchSlideCount = async () => {
      if (isCreateMode) return;

      try {
        const result = await slideApiService.searchSlides({
          shopId,
          locationId: location.id,
          page: 0,
          size: 1  // Minimum allowed size - we only need the count from metadata
        });
        setSlideCount(result.metadata.totalElements);
      } catch (error) {
        console.error('Error fetching slide count:', error);
        setSlideCount(undefined);
      }
    };

    fetchSlideCount();
  }, [shopId, location.id, isCreateMode]);

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      setError('');
      await shopLocationService.deleteShopLocation(shopId, location.id);

      // Notify parent and close modal
      onLocationUpdated(location); // Parent will refresh and see location is gone
      onClose();
    } catch (err: any) {
      console.error('Error deleting location:', err);

      // Check if error response has a specific message header
      if (err.response?.headers?.['x-error-message']) {
        setError(err.response.headers['x-error-message']);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to delete location');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate required fields
      if (!name.trim()) {
        setError('Location name is required');
        return;
      }

      // Step 1: Apply image rotations if any (only in edit mode)
      const rotationsToApply = Object.entries(imageRotations).filter(([_, degrees]) => degrees !== 0);

      if (!isCreateMode && rotationsToApply.length > 0) {
        setRotatingImages(true);
        console.log(`Applying rotations to ${rotationsToApply.length} location image(s)...`);

        for (const [imageId, degrees] of rotationsToApply) {
          try {
            await shopLocationService.rotateLocationImage(shopId, location.id, imageId, degrees);
            console.log(`Rotated location image ${imageId} by ${degrees}°`);
          } catch (rotError) {
            console.error(`Failed to rotate location image ${imageId}:`, rotError);
            // Continue with other rotations even if one fails
          }
        }

        setRotatingImages(false);
      }

      // Step 1.5: Apply image crops if any (only in edit mode)
      const cropsToApply = Object.entries(imageCrops);

      if (!isCreateMode && cropsToApply.length > 0) {
        setCroppingImages(true);
        console.log(`Applying crops to ${cropsToApply.length} location image(s)...`);

        for (const [imageId, cropArea] of cropsToApply) {
          try {
            await shopLocationService.cropLocationImage(shopId, location.id, imageId, cropArea);
            console.log(`Cropped location image ${imageId}`);
          } catch (cropError) {
            console.error(`Failed to crop location image ${imageId}:`, cropError);
            // Continue with other crops even if one fails
          }
        }

        setCroppingImages(false);
      }

      // Step 2: Create or update location data
      let updatedLocation: ShopLocation;

      if (isCreateMode) {
        // Stop camera before submitting
        imageCaptureRef.current?.stopCamera();

        const createData: Partial<ShopLocation> = {
          name: name.trim(),
          description: description.trim()
        };
        console.log('Creating new location:', createData, `with ${selectedFiles.length} image(s)`);
        updatedLocation = await shopLocationService.createShopLocation(shopId, createData, selectedFiles.length > 0 ? selectedFiles : undefined);
        console.log('Location created successfully:', updatedLocation);
      } else {
        // For edit mode, include images
        const updateData: Partial<ShopLocation> = {
          name: name.trim(),
          description: description.trim(),
          images: images
        };
        console.log('Updating location:', updateData);
        updatedLocation = await shopLocationService.updateShopLocation(
          shopId,
          location.id,
          updateData
        );
        console.log('Location updated successfully:', updatedLocation);
      }

      // If we rotated or cropped any images, fetch fresh data
      let finalLocation: ShopLocation;
      if (!isCreateMode && (rotationsToApply.length > 0 || cropsToApply.length > 0)) {
        console.log('Fetching fresh location data after image transformations...');
        finalLocation = await shopLocationService.getShopLocation(shopId, location.id);
      } else {
        finalLocation = updatedLocation;
      }

      // Notify parent of update
      onLocationUpdated(finalLocation);
      onClose();
    } catch (err) {
      console.error('Error updating location:', err);
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update location');
      }
    } finally {
      setLoading(false);
      setRotatingImages(false);
      setCroppingImages(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        ...(isMobileCameraActive ? { visibility: 'hidden' as const } : {})
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {isCreateMode ? 'Create Location' : 'Edit Location'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: '0',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: 'var(--error-bg)',
                border: '1px solid var(--error-border)',
                borderRadius: '6px',
                color: 'var(--error-text)',
                marginBottom: '20px',
                fontSize: '14px'
              }}
            >
              {error}
            </div>
          )}

          {/* Name field */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            >
              Location Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Warehouse, Storage Room, etc."
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
              maxLength={100}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {name.length}/100 characters
            </div>
          </div>

          {/* Description field */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            >
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this location..."
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                minHeight: '100px',
                resize: 'vertical'
              }}
              maxLength={500}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {description.length}/500 characters
            </div>
          </div>

          {/* Images section - only in edit mode */}
          {!isCreateMode && (
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
              >
                Images (Max 5)
              </label>
              <LocationImageManager
                shopId={shopId}
                locationId={location.id}
                images={images}
                onImagesUpdate={setImages}
                onRotationsUpdate={setImageRotations}
                onCropsUpdate={setImageCrops}
                onCameraStateChange={handleCameraStateChange}
              />
            </div>
          )}

          {/* Image capture in create mode */}
          {isCreateMode && (
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '12px',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
              >
                Images (Max {MAX_IMAGES}) - Optional
              </label>
              <ImageCapture
                ref={imageCaptureRef}
                selectedFiles={selectedFiles}
                onFilesChange={setSelectedFiles}
                maxImages={MAX_IMAGES}
                onError={setError}
                onCameraStateChange={handleCameraStateChange}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 24px',
            borderTop: '1px solid var(--border-light)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'space-between'
          }}
        >
          {/* Delete button - always show in edit mode, but disabled until check confirms deletion is allowed */}
          <div>
            {!isCreateMode && (
              <button
                onClick={handleDelete}
                disabled={deleting || loading || slideCount === undefined || slideCount > 0 || (location.places?.length || 0) > 0}
                title={
                  slideCount === undefined ? 'Checking if location can be deleted...' :
                  slideCount > 0 ? `Cannot delete: ${slideCount} slide(s) associated with this location` :
                  (location.places?.length || 0) > 0 ? `Cannot delete: ${location.places?.length} place(s) in this location` :
                  'Delete this location'
                }
                style={{
                  padding: '10px 20px',
                  border: '1px solid var(--error)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--error-text)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: deleting || loading || slideCount === undefined || slideCount > 0 || (location.places?.length || 0) > 0 ? 'not-allowed' : 'pointer',
                  opacity: deleting || loading || slideCount === undefined || slideCount > 0 || (location.places?.length || 0) > 0 ? 0.4 : 1
                }}
              >
                {deleting ? 'Deleting...' : slideCount === undefined ? 'Delete Location' : 'Delete Location'}
              </button>
            )}
          </div>

          {/* Right-side buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              disabled={loading || deleting}
              style={{
                padding: '10px 20px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading || deleting ? 'not-allowed' : 'pointer',
                opacity: loading || deleting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || deleting || !name.trim()}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: loading || deleting || !name.trim() ? 'var(--disabled-bg)' : 'var(--primary)',
                color: loading || deleting || !name.trim() ? 'var(--disabled)' : 'var(--text-inverse)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading || deleting || !name.trim() ? 'not-allowed' : 'pointer'
              }}
            >
{croppingImages ? 'Cropping Images...' : rotatingImages ? 'Rotating Images...' : loading ? (isCreateMode ? 'Creating...' : 'Saving...') : (isCreateMode ? 'Create Location' : 'Save Changes')}
            </button>
          </div>
        </div>
      </div>

      {/* Rate Limit Modal */}
      {rateLimitError && (
        <RateLimitModal
          error={rateLimitError}
          onClose={() => setRateLimitError(null)}
        />
      )}
    </div>
  );
};

export default LocationEditModal;
